import { useState, useEffect, useRef } from 'react'
import { createChart, CrosshairMode } from 'lightweight-charts';
import { __fetching_data__ } from '../../components/api';
import { useIsMobile, useIsPortraitTablet } from '../../components/hooks';

// ============================================================
// SKELETON
// ============================================================

const skeletonCSS = `
@keyframes s50Shimmer {
    0%   { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
}
`;

function Shimmer({ delay = 0 }) {
    return (
        <div style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'linear-gradient(90deg, transparent 0%, rgba(56,189,248,0.07) 40%, rgba(125,211,252,0.15) 50%, rgba(56,189,248,0.07) 60%, transparent 100%)',
            animation: `s50Shimmer 1.8s ease-in-out ${delay}s infinite`,
        }} />
    );
}

/** Skeleton for a single ChartCard — mirrors exact dimensions */
function ChartCardSkeleton({ title, color = '#334155', delay = 0 }) {
    return (
        <div style={{
            backgroundColor: '#111827',
            border: '1px solid #334155',
            borderRadius: 12,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
        }}>
            {/* card header */}
            <div style={{
                padding: '10px 16px',
                backgroundColor: '#0f172a',
                borderBottom: '1px solid rgba(51,65,85,0.5)',
                display: 'flex',
                alignItems: 'center',
            }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#cbd5e1' }}>{title}</span>
            </div>

            {/* chart area skeleton — height matches real chart (320px) */}
            <div style={{ width: '100%', height: 320, backgroundColor: '#0f172a', position: 'relative', overflow: 'hidden' }}>
                <style>{skeletonCSS}</style>

                {/* fake grid lines */}
                <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} preserveAspectRatio="none">
                    {/* horizontal */}
                    {[20, 38, 56, 74].map((pct, i) => (
                        <line key={`h${i}`} x1="0" y1={`${pct}%`} x2="100%" y2={`${pct}%`} stroke="#1e293b" strokeWidth="1" />
                    ))}
                    {/* vertical */}
                    {[16, 33, 50, 67, 84].map((pct, i) => (
                        <line key={`v${i}`} x1={`${pct}%`} y1="0" x2={`${pct}%`} y2="100%" stroke="#1e293b" strokeWidth="1" />
                    ))}
                    {/* fake price line — unique shape per chart */}
                    <polyline
                        points={
                            color === '#32D74B'
                                ? "0,240 80,220 160,230 240,180 320,195 400,150 480,165 560,130 640,145 720,110 800,125 900,95 1000,110 1100,85 1200,100 1300,75"
                                : color === '#FFCC00'
                                ? "0,180 100,160 200,175 300,140 400,160 500,125 600,145 700,115 800,135 900,105 1000,125 1100,95"
                                : color === '#FC6238'
                                ? "0,200 100,175 200,190 300,155 400,170 500,135 600,155 700,120 800,140 900,110 1000,130"
                                : "0,160 100,145 200,165 300,130 400,150 500,115 600,140 700,108 800,128 900,95 1000,118"
                        }
                        fill="none"
                        stroke={color}
                        strokeOpacity="0.15"
                        strokeWidth="2"
                        vectorEffect="non-scaling-stroke"
                    />
                </svg>

                {/* watermark label */}
                <div style={{
                    position: 'absolute', inset: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'rgba(255,255,255,0.04)', fontSize: 22, fontWeight: 'bold',
                    pointerEvents: 'none', userSelect: 'none',
                }}>
                    {title}
                </div>

                {/* shimmer sweep */}
                <Shimmer delay={delay} />
            </div>
        </div>
    );
}

// ============================================================
// CHART CARD WRAPPER (real)
// ============================================================
function ChartCard({ title, chartContainerRef, isLoading, skeletonColor, skeletonDelay }) {
    return (
        <div style={{
            backgroundColor: '#111827',
            border: '1px solid #334155',
            borderRadius: '12px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
        }}>
            <div style={{
                padding: '10px 16px',
                backgroundColor: '#0f172a',
                borderBottom: '1px solid rgba(51,65,85,0.5)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#cbd5e1' }}>{title}</span>
            </div>

            {/* skeleton overlay — absolute, z above chart */}
            {isLoading && (
                <div style={{ position: 'absolute', top: 41, left: 0, right: 0, bottom: 0, zIndex: 10 }}>
                    <div style={{ width: '100%', height: '100%', backgroundColor: '#0f172a', position: 'relative', overflow: 'hidden' }}>
                        <style>{skeletonCSS}</style>
                        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} preserveAspectRatio="none">
                            {[20, 38, 56, 74].map((pct, i) => (
                                <line key={`h${i}`} x1="0" y1={`${pct}%`} x2="100%" y2={`${pct}%`} stroke="#1e293b" strokeWidth="1" />
                            ))}
                            {[16, 33, 50, 67, 84].map((pct, i) => (
                                <line key={`v${i}`} x1={`${pct}%`} y1="0" x2={`${pct}%`} y2="100%" stroke="#1e293b" strokeWidth="1" />
                            ))}
                            <polyline
                                points={
                                    skeletonColor === '#32D74B'
                                        ? "0,240 80,220 160,230 240,180 320,195 400,150 480,165 560,130 640,145 720,110 800,125 900,95 1000,110 1100,85 1200,100 1300,75"
                                        : skeletonColor === '#FFCC00'
                                        ? "0,180 100,160 200,175 300,140 400,160 500,125 600,145 700,115 800,135 900,105 1000,125 1100,95"
                                        : skeletonColor === '#FC6238'
                                        ? "0,200 100,175 200,190 300,155 400,170 500,135 600,155 700,120 800,140 900,110 1000,130"
                                        : "0,160 100,145 200,165 300,130 400,150 500,115 600,140 700,108 800,128 900,95 1000,118"
                                }
                                fill="none" stroke={skeletonColor} strokeOpacity="0.15" strokeWidth="2" vectorEffect="non-scaling-stroke"
                            />
                        </svg>
                        <div style={{
                            position: 'absolute', inset: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'rgba(255,255,255,0.04)', fontSize: 22, fontWeight: 'bold',
                            pointerEvents: 'none', userSelect: 'none',
                        }}>
                            {title}
                        </div>
                        <Shimmer delay={skeletonDelay} />
                    </div>
                </div>
            )}

            {/* chart container — always mounted */}
            <div
                ref={chartContainerRef}
                style={{
                    width: '100%',
                    height: 320,
                    backgroundColor: '#0f172a',
                    opacity: isLoading ? 0 : 1,
                    transition: 'opacity 0.3s ease',
                }}
            />
        </div>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================
const S50Page = () => {
    const isMobile  = useIsMobile();
    const isPTablet = useIsPortraitTablet();

    const [inputValue] = useState('SET50');
    const [isLoading, setIsLoading] = useState(true);

    const containerRef0 = useRef(null);
    const containerRef1 = useRef(null);
    const containerRef2 = useRef(null);
    const containerRef3 = useRef(null);

    const chartInstancesRef  = useRef([]);
    const seriesListRef      = useRef([]);
    const isSyncingZoom      = useRef(false);
    const isSyncingCrosshair = useRef(false);

    const base_api_url =
        process.env.NODE_ENV === "production"
            ? window.location.protocol === "https:"
                ? process.env.REACT_APP_API_HTTPS_URL
                : process.env.REACT_APP_API_HTTP_URL
            : "/application";

    // ── helpers ──────────────────────────────────────────────
    function generateData(data, dateCol = 'date', field = 'value') {
        if (!data || !data.length) return [];
        const offset = 7 * 60 * 60 * 1000;
        return data.map(d => ({
            time:  (new Date(d[dateCol]).getTime() + offset) / 1000,
            value: parseFloat(d[field]) || 0,
        }));
    }

    function syncAndFilterData(dataSets, dateKeys) {
        const processed = dataSets.map((ds, i) =>
            ds.map(item => ({
                ...item,
                __d: item[dateKeys[i]] ? new Date(item[dateKeys[i]]) : null,
            })).filter(item => item.__d instanceof Date && !isNaN(item.__d))
        );

        const sets = processed.map(ds =>
            new Set(ds.map(item => item.__d.toISOString().split('T')[0]))
        );

        const common = [...sets.reduce((a, b) => new Set([...a].filter(d => b.has(d))))];

        return processed.map(ds =>
            ds.filter(item => common.includes(item.__d.toISOString().split('T')[0]))
               .map(({ __d, ...rest }) => rest)
        );
    }

    // ── cleanup ───────────────────────────────────────────────
    const cleanupCharts = () => {
        chartInstancesRef.current.forEach(c => { try { c.remove(); } catch (_) {} });
        chartInstancesRef.current = [];
        seriesListRef.current     = [];
    };

    // ── fetch ─────────────────────────────────────────────────
    const fetchingData = async () => {
        setIsLoading(true);
        try {
            const response = await __fetching_data__({
                url: `${base_api_url}/chat_bot_report`,
                method: "POST",
                body: JSON.stringify({ user_input: inputValue, timeframe: 'Day' }),
            });
            // if (response.status && response.data?.index_data) {
            //     renderCharts(response.data);
            // }
            console.log(response)
            if (response.status) {
                renderCharts(response.data);
            }
        } catch (err) {
            console.error('Error fetching data:', err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchingData();
        return cleanupCharts;
    }, []);

    // ── render charts ─────────────────────────────────────────
    const renderCharts = (allData) => {
        cleanupCharts();

        // const plotData = allData.index_data;
        const plotData = allData;

        const raw1 = plotData['s50_ind']['flow_s50'];
        const raw2 = plotData['s50_ind']['mid_trend'];
        // const raw3 = plotData['s50_ind']['mksum_s50'][0];
        const raw3 = plotData['s50_ind']['mksum_s50'];
        const raw4 = plotData['price'][0];

        const [fd1, fd2, fd3, fd4] = syncAndFilterData(
            [raw1, raw2, raw3, raw4],
            ['time', 'date', 'time', 'time']
        );

        const CONFIGS = [
            { ref: containerRef0, color: '#32D74B', label: 'Last',                data: generateData(fd4, 'time', 'value'),  isPrice: true  },
            { ref: containerRef1, color: '#FFCC00', label: 'Confirm Up/Down S50', data: generateData(fd1, 'time', 'value'),  isPrice: false },
            { ref: containerRef2, color: '#FC6238', label: 'Trend',               data: generateData(fd3, 'time', 'value'),  isPrice: false },
            { ref: containerRef3, color: '#FF6FFF', label: 'Mid Trend',           data: generateData(fd2, 'date', 'close'),  isPrice: false },
        ];

        const charts = [];
        const series = [];

        CONFIGS.forEach(({ ref, color, label, data, isPrice }) => {
            if (!ref.current) return;
            ref.current.innerHTML = '';

            const chart = createChart(ref.current, {
                width:  ref.current.offsetWidth,
                height: 320,
                layout: {
                    attributionLogo: false,
                    background: { type: 'solid', color: '#0f172a' },
                    textColor: '#94a3b8',
                },
                grid: {
                    horzLines: { color: '#1e293b' },
                    vertLines: { color: '#1e293b' },
                },
                crosshair: { mode: CrosshairMode.Normal },
                timeScale: { visible: true },
                watermark: {
                    visible: true,
                    fontSize: 22,
                    horzAlign: 'center',
                    vertAlign: 'center',
                    color: 'rgba(255,255,255,0.04)',
                    text: label,
                },
            });

            const s = chart.addLineSeries({
                color,
                lastValueVisible: isPrice,
                priceLineVisible: false,
            });
            s.setData(data);

            if (!isPrice) s.applyOptions({ priceFormat: { type: 'volume' } });

            const ro = new ResizeObserver(() => {
                if (ref.current) chart.applyOptions({ width: ref.current.offsetWidth });
            });
            ro.observe(ref.current);

            setTimeout(() => chart.timeScale().applyOptions({ rightOffset: 5 }), 100);

            charts.push(chart);
            series.push(s);
        });

        chartInstancesRef.current = charts;
        seriesListRef.current     = series;

        // ── Sync: Zoom ────────────────────────────────────────
        charts.forEach((src, i) => {
            src.timeScale().subscribeVisibleLogicalRangeChange(range => {
                if (!range || isSyncingZoom.current) return;
                isSyncingZoom.current = true;
                charts.forEach((dst, j) => {
                    if (i !== j) dst.timeScale().setVisibleLogicalRange(range);
                });
                isSyncingZoom.current = false;
            });
        });

        // ── Sync: Crosshair ───────────────────────────────────
        charts.forEach((src, i) => {
            src.subscribeCrosshairMove(param => {
                if (isSyncingCrosshair.current) return;
                isSyncingCrosshair.current = true;

                const hasPoint = param.time !== undefined && param.point !== undefined;

                charts.forEach((dst, j) => {
                    if (i === j) return;
                    if (hasPoint) {
                        const srcVal = param.seriesData.get(series[i])?.value ?? 0;
                        try { dst.setCrosshairPosition(srcVal, param.time, series[j]); } catch (_) {}
                    } else {
                        try { dst.clearCrosshairPosition(); } catch (_) {}
                    }
                });

                isSyncingCrosshair.current = false;
            });
        });
    };

    // ── render ────────────────────────────────────────────────
    const CARD_META = [
        { title: 'Last',                ref: containerRef0, color: '#32D74B', delay: 0    },
        { title: 'Confirm Up/Down S50', ref: containerRef1, color: '#FFCC00', delay: 0.15 },
        { title: 'Trend',               ref: containerRef2, color: '#FC6238', delay: 0.30 },
        { title: 'Mid Trend',           ref: containerRef3, color: '#FF6FFF', delay: 0.45 },
    ];

    return (
        <div style={{
            width: '100%',
            height: 'calc(100vh - var(--top-bar-height, 0px))',
            backgroundColor: '#0b111a',
            color: 'white',
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            boxSizing: 'border-box',
            overflowY: 'auto',
        }}>
            <div style={{
                display: 'grid',
                gridTemplateColumns: (isMobile || isPTablet) ? '1fr' : 'repeat(2, 1fr)',
                gap: 16,
                flex: 1,
            }}>
                {CARD_META.map(({ title, ref, color, delay }) => (
                    <ChartCard
                        key={title}
                        title={title}
                        chartContainerRef={ref}
                        isLoading={isLoading}
                        skeletonColor={color}
                        skeletonDelay={delay}
                    />
                ))}
            </div>
        </div>
    );
};

export default S50Page;